/**
 * FEEDBACK WIDGET — BOUTON FLOTTANT (TICKET-CRM-14)
 * Monté une fois dans Layout (branche dashboard, donc staff authentifié).
 * - démarre la capture debug au mount (console/actions/réseau) ;
 * - pousse la navigation react-router dans le tracker (pas de popstate sur
 *   pushState) ;
 * - enregistre le provider de contexte 'auth' (id technique uniquement,
 *   jamais de champ nominatif — spec §6).
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../hooks/useAuth';
import { debugCaptureService } from '../../lib/feedback/debug-capture';
import { userActionsTracker } from '../../lib/feedback/user-actions-tracker';
import {
  registerContextProvider,
  unregisterContextProvider,
} from '../../lib/feedback/context-registry';
import { FeedbackDialog } from './FeedbackDialog';

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  // Capture active pour toute la session (jamais destroy au unmount).
  useEffect(() => {
    debugCaptureService.initialize();
  }, []);

  useEffect(() => {
    userActionsTracker.trackNavigation(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    registerContextProvider('auth', () => ({ userId: user?.id ?? null }));
    return () => unregisterContextProvider('auth');
  }, [user?.id]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Signaler un problème"
        title="Signaler un problème"
        className={cn(
          'fixed right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-zinc-900 text-white shadow-lg transition-transform hover:scale-105 hover:bg-zinc-800',
          // En dev, le QueryMonitor (dev-only) occupe le coin bas-droit : on
          // se décale au-dessus pour rester cliquable.
          import.meta.env.DEV ? 'bottom-24' : 'bottom-6'
        )}
      >
        <Bug className="h-5 w-5" />
      </button>

      <FeedbackDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
